// Canonical engine wire emit for an individual WhatsApp message.
//
// The extension's `createLeanMessage` projects WAWeb's raw Msg model
// → this slim shape at the IPC boundary:
//   - Collapses wid objects to `_serialized` strings.
//   - Drops `__x_*` internals.
//   - Fills `author` even for outgoing (using the cached self-JID
//     from wa.User.getMaybeMePnUser) so consumers don't have to
//     branch on direction to figure out "who sent this".
//   - Keeps WAWeb's `from`/`to` semantics on the wire (sender / recipient
//     respectively).
//
// The RAW WAWeb Msg shape (~150 fields, wid objects, internal flags)
// lives inside the chrome extension per #74. bt-common only exposes
// the slim/wire form.
//
// WAWeb Msg semantics (verified via window.getStore probe 2026-05-22):
//   msg.from = SENDER JID (self for outgoing, peer/group for incoming)
//   msg.to   = RECIPIENT JID (peer for outgoing, self for incoming)
//   msg.id   = key with fromMe + _serialized + remote
//   msg.author = the PARTICIPANT who sent inside a group; undefined
//                for DMs. ONLY set on incoming group messages.

import { z } from 'zod';
import { WireJidSchema } from '../../contract/wire-identity';
import { BtSlimMessageKeySchema } from './message-key';
import { BtSlimLinkPreviewSchema } from './link-preview';
import { BtSlimPollSchema } from './poll';
import { WA_INTERACTIVE_KINDS } from './message-type-map';

// ── Quoted (reply-target) message ────────────────────────────────────
//
// When a message is a reply, WAWeb stores a reference to the quoted
// message on the reply itself (`quotedStanzaID` / `quotedParticipant` /
// `quotedRemoteJid` + an embedded `quotedMsg` preview object). The engine
// reconstructs the quoted message's canonical key from those raw fields
// (verified against the live store 2026-07-03): the quoted message is
// frequently NOT loaded in the local store, so the key is rebuilt from
// the reference fields rather than looked up:
//   - id          = quotedStanzaID
//   - remote      = quotedRemoteJid ?? the reply's own chat JID (a reply
//                   quotes a message in the same chat; quotedRemoteJid is
//                   null for same-chat quotes, which is the common case)
//   - participant = quotedParticipant, but only meaningful (and only part
//                   of `_serialized`) for group chats — omitted in DMs
//   - fromMe      = WAWebQuotedMsgModelUtils.isMyQuotedMsg(reply)
// `text`/`type`/`author` are a lightweight preview of the quoted message
// so consumers can render the reply context without a follow-up fetch.
// Absent when the message is not a reply.
export const BtSlimQuotedMessageSchema = z.object({
  /** The reply target's canonical key — pass to message-key endpoints. */
  waMessageKey: BtSlimMessageKeySchema,
  /** Preview text of the quoted message (its body or media caption). */
  text: z.string().optional(),
  /** The quoted message's kind (chat/image/video/…). */
  type: z.string().optional(),
  /** Sender JID of the quoted message (@c.us), best-effort; null when unresolved. */
  author: WireJidSchema.nullable().optional(),
});
export type BtSlimQuotedMessage = z.infer<typeof BtSlimQuotedMessageSchema>;

// ── Interactive (menu options / selected option) ─────────────────────
export const BtSlimInteractiveOptionSchema = z.object({
  id: z.string().nullable(),
  title: z.string().nullable(),
  description: z.string().optional(),
  section: z.string().optional(),
});
export const BtSlimInteractiveSchema = z.object({
  kind: z.enum(WA_INTERACTIVE_KINDS),
  /** Menu: the choices on offer. */
  options: z.array(BtSlimInteractiveOptionSchema).optional(),
  /** Click: the id / text of the option picked and the id of the menu it answers. */
  selectedId: z.string().optional(),
  selectedTitle: z.string().optional(),
  quotedId: z.string().optional(),
});
export type BtSlimInteractive = z.infer<typeof BtSlimInteractiveSchema>;

// ── Call (a `call_log` row) ──────────────────────────────────────────
//
// What the call was, so a reader (an agent, the Team Inbox) can tell a call
// happened without decoding the engine's native record. Both engines fill it
// from WhatsApp's CallLogMessage (WAProto `Message.CallLogMessage`): Baileys
// from the proto / its missed-call stubs, the extension from WA Web's
// `call_log` Msg.
export const BT_CALL_OUTCOMES = ['answered', 'missed', 'rejected', 'unknown'] as const;
export type BtCallOutcome = (typeof BT_CALL_OUTCOMES)[number];
export const BtSlimCallSchema = z.object({
  /** `in` = the contact called us, `out` = we called them. */
  direction: z.enum(['in', 'out']),
  media: z.enum(['voice', 'video']),
  outcome: z.enum(BT_CALL_OUTCOMES),
  /** Talk time in whole seconds; present only for an answered call. */
  durationSec: z.number().int().nonnegative().optional(),
  isGroup: z.boolean(),
});
export type BtSlimCall = z.infer<typeof BtSlimCallSchema>;

/**
 * WAProto `CallLogMessage.CallOutcome` → the contract outcome. CONNECTED (0) is
 * answered; MISSED (1) and the silenced ones (6 DND, 7 unknown caller — WA shows
 * them as missed) are missed; REJECTED (3) is rejected. FAILED (2),
 * ACCEPTED_ELSEWHERE (4), ONGOING (5) and anything newer are unknown.
 */
export function callOutcomeFromProto(outcome: number | null | undefined): BtCallOutcome {
  switch (outcome) {
    case 0: return 'answered';
    case 1: case 6: case 7: return 'missed';
    case 3: return 'rejected';
    default: return 'unknown';
  }
}

/**
 * A call as one compact line for an agent's chat context:
 * `📞 call: in/voice/missed, 0s` (`, group` for a group call). A row from an
 * engine that does not fill `call` yet still reads as a call: `📞 call`.
 */
export function formatCallLine(call: BtSlimCall | null | undefined): string {
  if (!call) return '📞 call';
  return `📞 call: ${call.direction}/${call.media}/${call.outcome}, ${call.durationSec ?? 0}s${call.isGroup ? ', group' : ''}`;
}

// ── Reaction ─────────────────────────────────────────────────────────
//
// A single emoji reaction on a message. WAWeb keeps reactions OUT of the
// Msg model (in a parallel `AddonReactionTable` keyed by the reacted-to
// message's key), so the engine fetches them separately and attaches an
// array of these to the message projection. `from` is the reactor's JID
// (@lid→@c.us converted like every other id on the wire); `t` is unix
// seconds (WA stores reaction timestamps in ms — the engine downscales).
// Reaction removals (empty reactionText in the store) are dropped, so the
// array only ever carries live reactions. Omitted entirely when a message
// has none.
export const BtSlimReactionSchema = z.object({
  /** The reaction emoji (e.g. "👍"). */
  text: z.string(),
  /** Reactor's JID (@c.us), best-effort. */
  from: WireJidSchema.optional(),
  /** True when the connected account is the reactor. */
  fromMe: z.boolean().optional(),
  /** Reaction timestamp in unix seconds, when the store carries one. */
  t: z.number().optional(),
});
export type BtSlimReaction = z.infer<typeof BtSlimReactionSchema>;

// ── Closed message type ──────────────────────────────────────────────
//
// The contract list. Every engine maps its native type onto it in its own
// shaper; a native type with no mapping (album, list, buttons, …) is
// `unknown`. A GIF is `video` + `isGif: true`. A reaction is never a row. The
// contract runner checks every emitted `type` is in the list; the runtime parse
// stays `z.string()` so older engines' values pass unchanged.
//
// Engine-neutral categories for non-content rows (native → contract):
//   notification  system notice. WAWeb: e2e_notification, protocol,
//                 newsletter_notification, broadcast_notification,
//                 gp2_notification (a content-less group notice, distinct from
//                 gp2). Baileys: stub, protocol and ephemeral-setting messages.
//   ciphertext    decryption placeholder, later replaced under the same key —
//                 never ledgered. WAWeb: ciphertext. Baileys: the CIPHERTEXT stub.
//   group_invite  group invite card. WAWeb: groups_v4_invite. Baileys:
//                 groupInviteMessage.
//   gp2           group event (join/leave/subject/settings) — as today.
export const BT_SLIM_MESSAGE_TYPES = [
  'chat', 'image', 'video', 'audio', 'ptt', 'document', 'sticker', 'ptv',
  'poll_creation', 'vcard', 'multi_vcard', 'location', 'live_location', 'gp2',
  'revoked', 'call_log', 'notification_template', 'notification', 'ciphertext',
  'group_invite', 'unknown',
] as const;
export const BtSlimMessageTypeSchema = z.enum(BT_SLIM_MESSAGE_TYPES);
export type BtSlimMessageType = z.infer<typeof BtSlimMessageTypeSchema>;

/** The one definition of a SYSTEM message — no customer-authored content:
 *  it never starts an agent/chatbot turn. A `call_log` row still shows in the
 *  agent's chat context, as a `formatCallLine` line. */
const SYSTEM_MESSAGE_TYPES: ReadonlySet<string> = new Set<BtSlimMessageType>([
  'gp2', 'notification', 'ciphertext', 'revoked', 'call_log', 'notification_template', 'group_invite',
]);
export function isSystemMessageType(type: string | null | undefined): boolean {
  return !!type && SYSTEM_MESSAGE_TYPES.has(type);
}

/**
 * One gp2 templateParam as a string (#108). A Wid object becomes its jid (`_serialized`, else
 * `user@server`); a number/boolean is stringified. Anything else becomes '' so the positions of the
 * other params (consumers read `[0]`) stay put, and no raw object content reaches the wire.
 */
function coerceTemplateParam(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value && typeof value === 'object') {
    const wid = value as { _serialized?: unknown; user?: unknown; server?: unknown };
    if (typeof wid._serialized === 'string') return wid._serialized;
    if (typeof wid.user === 'string' && typeof wid.server === 'string') return `${wid.user}@${wid.server}`;
  }
  return '';
}

/**
 * gp2 `recipients` as jid strings (#513). Engine builds before wire-contract 3.8 emit WAWeb Wid objects
 * (`{_serialized}` / `{user, server}`); each becomes its jid. An entry holding no jid is dropped, never invented,
 * so one odd participant can't fail the whole read. Anything that is not an array is left for the schema to reject.
 */
function coerceRecipients(value: unknown): unknown {
  if (!Array.isArray(value)) return value;
  const jids: string[] = [];
  for (const entry of value) {
    if (typeof entry === 'string') {
      jids.push(entry);
      continue;
    }
    if (!entry || typeof entry !== 'object') continue;
    const wid = entry as { _serialized?: unknown; user?: unknown; server?: unknown };
    if (typeof wid._serialized === 'string' && wid._serialized.length > 0) jids.push(wid._serialized);
    else if (typeof wid.user === 'string' && typeof wid.server === 'string') jids.push(`${wid.user}@${wid.server}`);
  }
  return jids;
}

// ── Slim wire variant ────────────────────────────────────────────────

export const BtSlimMessageSchema = z.object({
  /**
   * Normalized message key for engine round-trips and the single source
   * of truth for the message's identity. `remote` is the canonical chat
   * JID regardless of direction (more reliable than `from` for chat-id
   * resolution); `id` is the bare message id; `_serialized` is the full
   * `"<fromMe>_<chatJid>_<id>[_<participant>]"` wire key; `participant`
   * is set on group incoming messages — the sender's @lid or @c.us JID.
   * The public API exposes this object as `waMessageKey`.
   *   Example outgoing `_serialized`: `true_972528611660@c.us_3EB06273748E187EAEF313`
   *   Example incoming-group `_serialized`: `false_120363@g.us_AC4725..._140561931612210@lid`
   *
   * (#) The redundant `id: {_serialized}` field was removed — it only
   * duplicated `waMessageKey._serialized`.
   */
  waMessageKey: BtSlimMessageKeySchema.optional(),
  /**
   * Message body — text for `type: 'chat'`; empty string for media
   * (the user-visible text on media lives in `caption`). For
   * `type: 'poll_creation'` slim moves the question into `body`
   * for convenience (matches the LLM-friendly read). Always emitted
   * — empty string means "no text" (canonical: no undefined).
   */
  body: z.string(),
  /**
   * Caption text on media messages (image/video/document). `null`
   * for media without a caption and for non-media types — canonical
   * absent marker (no undefined).
   */
  caption: z.string().nullable(),
  /**
   * Filename for document-type messages. The only discriminator for
   * documents (body is empty, caption optional). `null` for
   * non-document types.
   */
  filename: z.string().nullable(),
  /** Poll question + options when `type: 'poll_creation'`. */
  poll: BtSlimPollSchema.optional(),
  /**
   * SENDER JID (WAWeb's Msg.from semantics):
   *   outgoing      → self JID (e.g. "972544325389@c.us")
   *   incoming DM   → peer JID
   *   incoming group → group JID (sender lives in `author`)
   */
  from: WireJidSchema.optional(),
  /**
   * Explicit sender JID. Always points at the human/entity that sent
   * the message — no direction branching:
   *   outgoing       → self JID (engine fills from cached User store)
   *   incoming group → msg.author (participant JID)
   *   incoming DM    → undefined (sender == peer == `from`)
   */
  author: WireJidSchema.nullable().optional(),
  /** Direction. `true` for messages sent by the connected account. */
  fromMe: z.boolean(),
  /**
   * Timestamp in unix seconds. WAWeb's Msg model always carries this;
   * backend transformer reads it to produce the public `timestamp`
   * field (ISO 8601). Example: 1779274661 (May 2026). Canonical
   * single source — the `timestamp` alias was dropped (#65).
   */
  t: z.number(),
  /**
   * Message kind — the closed BT_SLIM_MESSAGE_TYPES list; an unmapped value
   * parses as `unknown`. Observed WAWeb types (probe 2026-05-22, n=1471
   * messages across 200 chats):
   *   'chat'           — text (1012 / 69%)
   *   'image'          — has body=base64 thumbnail + mediaKey + caption?  (201)
   *   'video'          — duration is a STRING; isGif:boolean             (21)
   *   'audio'          — uploaded audio file                              (13)
   *   'ptt'            — push-to-talk voice note                          (13)
   *   'document'       — has filename + pageCount + caption?              (26)
   *   'sticker'        — image/webp                                       (7)
   *   'poll_creation'  — body is question; options live nested            (21)
   *   'gp2'            — group system event (join/leave/subject/etc.)    (62)
   *   'revoked'        — deleted-for-everyone; subtype='sender'          (10)
   *   'vcard'          — body contains BEGIN:VCARD…END:VCARD              (12)
   *   'location'       — lat/lng + body=base64 map thumbnail              (2)
   *   'call_log'       — voice/video call record                          (42)
   *   'reaction'       — separate model (chatlistPreview tracks it);
   *                      flagged on parent via hasReaction
   *   'protocol'       — encryption/session protocol (rare)
   *
   * `subtype` augments: 'url' (text with link preview), 'sender' (revoke),
   * 'ephemeral' (gp2 ephemeral mode change), and others.
   */
  // @5.0b strict BtSlimMessageTypeSchema at the parse.
  /** @deprecated v1 type string, still emitted and accepted. Use `waType` (WA Web name); this is derived from the table in schemas/wa/message-type-map.ts. */
  type: z.string(),
  /**
   * WhatsApp Web's own message type (MSG_TYPE) for this message, from the
   * bt-common type table (schemas/wa/message-type-map.ts, `waWebName`): e.g.
   * 'chat', 'image', 'buttons_response'. CANONICAL: internal logic branches on this;
   * the v1 `type` is a deprecated derived alias (removal after a sunset date). Optional: absent when the engine cannot classify the message.
   */
  waType: z.string().optional(),
  /** A GIF (`type: 'video'`). */
  isGif: z.boolean().nullish(),
  /**
   * Sub-discriminator on `type`. Most relevant value: 'url' on
   * `type: 'chat'` indicating a text message that triggered WhatsApp's
   * link-preview UI. Slim doesn't always emit this; consumers should
   * treat missing as "no special subtype".
   */
  subtype: z.string().nullable().optional(),
  /**
   * Push name of the SENDER for incoming messages (resolved by WAWeb
   * from the WhatsApp account's display name, not the local Contact
   * store). Distinct from `senderName` which is the engine's
   * contact-store lookup. Useful when contact isn't in the user's
   * address book (cold incoming from a group).
   */
  notifyName: z.string().nullable().optional(),
  /** True when the engine detected `mediaKey` on the raw msg. */
  hasAttachedMedia: z.boolean().optional(),
  /** Media byte size. */
  size: z.number().nullish(),
  /** Forwarded (the Team Inbox "Forwarded" label). */
  isForwarded: z.boolean().nullish(),
  /** A view-once message. */
  isViewOnce: z.boolean().nullish(),
  /** gp2 (group system event): the participants it names. */
  recipients: z.preprocess(coerceRecipients, z.array(WireJidSchema)).optional(),
  /**
   * gp2 (group system event): its template parameters, as strings. WAWeb passes some as Wid objects
   * (or numbers/booleans); those are coerced here, so one odd param can't fail the whole read (#108).
   */
  templateParams: z.array(z.preprocess(coerceTemplateParam, z.string())).nullish(),
  /**
   * MIME type of the attached media (e.g. `application/pdf`, `image/jpeg`,
   * `audio/ogg`). Projected straight from WAWeb's `Msg.mimetype`. Present only
   * on media messages; omitted for text. Powers the `mime` prefix filter on
   * message search / list_messages (invoice/document hunts) — without it the
   * only media discriminator on the wire is the coarse `type`.
   */
  mimetype: z.string().nullable().optional(),
  /**
   * Clip length in SECONDS for voice/audio messages (`type: 'ptt'` or
   * `'audio'`). Powers the chat-list preview's "0:24" duration label
   * (`lastMessage.duration`). Omitted for every other type. Integer seconds.
   */
  duration: z.number().int().optional(),
  /**
   * The message this one is a reply to. Present only for replies (WAWeb's
   * `hasQuotedMsg`); omitted otherwise. Carries the quoted message's
   * canonical `waMessageKey` (reconstructed from the reply's stored quote
   * reference) plus a light preview. See BtSlimQuotedMessageSchema.
   */
  quotedMessage: BtSlimQuotedMessageSchema.optional(),
  /**
   * Interactive menu / click (buttons, list, template, native flow). The message
   * keeps `type: 'chat'` with its text in `body` (menu: contentText; click: the
   * selected option's text) so existing consumers are unaffected; this block adds
   * the structure. Menu -> `options`; click -> `selectedId`/`selectedTitle`/`quotedId`
   * (`quotedId` = the menu message id). See schemas/wa/message-type-map.ts.
   */
  interactive: BtSlimInteractiveSchema.optional(),
  /** `call_log` only: direction, voice/video, outcome and talk time. See BtSlimCallSchema. */
  call: BtSlimCallSchema.optional(),
  /**
   * Emoji reactions on this message. WAWeb keeps reactions in a parallel
   * `AddonReactionTable` (not on the Msg model), so the query path fetches
   * them in one bulk store call per page and attaches them here. Omitted
   * when the message has no reactions. See BtSlimReactionSchema.
   */
  reactions: z.array(BtSlimReactionSchema).optional(),
  /**
   * Display name of the sender (group incoming messages only —
   * resolved via the engine's in-memory Contact store). Best-effort:
   * may be null when the contact isn't in the store yet.
   */
  senderName: z.string().nullish(),
  /**
   * Link preview attached to a text message (subtype === 'url' on
   * WAWeb's Msg model). Null for messages without a URL preview,
   * for media, and for inbound messages from older WhatsApp clients
   * that didn't generate preview metadata.
   *
   * Two emit shapes are accepted on the wire:
   *   - chrome (`createLeanMessage` → `projectLinkPreviewFields`) emits the
   *     preview fields FLAT at the message top level (WAWeb's native
   *     `title/description/matchedText/canonicalUrl/thumbnail/richPreviewType`
   *     names). They are declared below so they survive the boundary parse;
   *     the public `/v1` mapper folds them into the nested public shape.
   *   - baileys / future engines may emit the nested `linkPreview` object.
   * The mapper reads flat-or-nested so either engine works unchanged.
   */
  linkPreview: BtSlimLinkPreviewSchema.nullable().optional(),
  /** Flat WAWeb link-preview fields (chrome engine); the contract form is the
   *  nested `linkPreview`. @5.0b delete. */
  title: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  matchedText: z.string().nullable().optional(),
  canonicalUrl: z.string().nullable().optional(),
  thumbnail: z.string().nullable().optional(),
  richPreviewType: z.number().nullable().optional(),
  /**
   * Delivery ack: -1=error, 0=pending, 1=server, 2=device, 3=read,
   * 4=played. Null when the engine couldn't determine.
   */
  ack: z.number().int().nullable().optional(),
  // `chatId` removed (#66). The chat-the-message-belongs-to is the
  // single canonical `waMessageKey.remote` — engines must populate
  // that field for every message, including cross-chat search hits.
  // Chrome's normalizeWaMessageKey was extended to derive `remote`
  // from the parsed `_serialized` when the raw `id.remote` is
  // unreliable (newsletter / group-event types).
  /**
   * Pre-fetched media URL. Usually null; populated only when the
   * engine attaches a download URL inline (rare in slim mode —
   * callers normally do a follow-up waGetMessageMediaTool in `full` mode).
   */
  mediaUrl: z.string().nullable().optional(),
  /**
   * E2E messageSecret bytes from the WAWeb store. Diagnostic-only —
   * MessageDebugDialog reads this to inspect encryption state.
   * Not part of the public API surface; list paths drop it. Typed
   * `unknown` because it's a Uint8Array or {0:..,1:..} object form
   * depending on which WAWeb internal serializer touched it.
   */
  messageSecret: z.unknown().optional(),
  /**
   * The CALLER CORRELATION TOKEN carried on this message — the value whoever
   * sent it passed as `secret`, decoded back out of `messageSecret`.
   *
   * Unlike `messageSecret` above this IS part of the public surface: it is a
   * value a caller chose and handed us, so handing it back is the whole point.
   * Present only when the message carries our caller-token marker; absent for
   * native WhatsApp messages, whose `messageSecret` is E2E material and must
   * never be surfaced (see bt-common scheduling/markers.ts).
   */
  secret: z.string().optional(),
  /**
   * Which retrieval tier produced this row — THREE tiers:
   *   (absent)     ⇒ live WhatsApp store   (tier 1)   → 'WA' badge
   *   'vault'      ⇒ client-side IndexedDB vault (tier 2, merged in by the engine)
   *   'backup'     ⇒ server DB backup      (tier 3)   → 'DB' badge
   * `'whatsapp'` is the explicit live tag (normally the field is just absent).
   * Optional + additive so it survives the tool wire (the extension's live+vault
   * merge tags vault rows); consumers use it for the bt-debug WA/VAULT/DB badge.
   */
  // 'whatsapp' is a legacy explicit live stamp (absent = live). @5.0b delete.
  source: z.enum(['vault', 'backup', 'whatsapp']).optional(),
});
export type BtSlimMessage = z.infer<typeof BtSlimMessageSchema>;

/** Row kinds that are never listed: an album (the extension's list filter), a
 *  notification other than gp2 (notification, notification_template), a
 *  decryption placeholder, and a reaction row (the pod's). */
const UNLISTED_TYPES: ReadonlySet<string> = new Set(['album', 'notification', 'notification_template', 'ciphertext', 'reaction']);

/**
 * The one listable-row rule, applied by both engines on list and search: the
 * extension's filter (`chat-operations.ts:817-842`: no album, no notification
 * except gp2, no `unknown` row without content) plus the pod-only reaction rows.
 * `revoked` and `gp2` stay listable (consumers such as `/v1` filter gp2 themselves).
 */
export function isListableMessage(m: BtSlimMessage): boolean {
  if (UNLISTED_TYPES.has(m.type)) return false;
  if (m.type === 'unknown') {
    return (m.body ?? '').trim() !== '' || (m.caption ?? '').trim() !== '' || m.hasAttachedMedia === true;
  }
  return true;
}

// Tool-result envelopes (waQueryMessagesTool / waLoadMessagesFromPhoneTool)
// live in interfaces/whatsapp-tool-contracts.interface.ts. This file
// exports only the underlying domain shape (BtSlimMessageSchema).
